import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import Row from "../../../common/components/Row"
import { canAccessLeadTargeting } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import { MY_MLS_SEARCH_PARAMS } from "../settings/campaignNavigation"
import { useBrokerageProfileIntake } from "../settings/useBrokerageProfileIntake"
import BoostLeadsWithCampaignDialog from "./BoostLeadsWithCampaignDialog"

const HeaderRoot = styled(Row)(({ theme }) => ({
  width: "100%",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 2),
  borderRadius: 12,
  border: `1px solid ${
    theme.palette.colors?.brand200 ?? theme.palette.divider
  }`,
  borderLeftWidth: 4,
  borderLeftColor: theme.palette.colors?.brand500 ?? theme.palette.primary.main,
  background: `linear-gradient(135deg, ${
    theme.palette.colors?.brand50 ?? theme.palette.primary.light
  }40 0%, rgba(255, 255, 255, 0.92) 55%)`,
  boxShadow:
    "0 4px 14px -4px rgba(137, 47, 206, 0.18), 0 2px 6px -2px rgba(52, 64, 84, 0.08)",
  backdropFilter: "blur(12px)",
  [theme.breakpoints.down("sm")]: {
    flexWrap: "wrap",
    padding: theme.spacing(1.25, 1.5),
  },
}))

const IconBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  flexShrink: 0,
  borderRadius: 10,
  color: theme.palette.primary.contrastText,
  background: `linear-gradient(145deg, ${
    theme.palette.colors?.brand500 ?? theme.palette.primary.main
  }, ${theme.palette.colors?.brand700 ?? theme.palette.primary.dark})`,
  boxShadow: `0 2px 8px ${
    theme.palette.colors?.brand400 ?? theme.palette.primary.main
  }45`,
  "& .MuiSvgIcon-root": {
    color: "#fff",
  },
}))

const HeaderActionButton = styled(Button)(({ theme }) => ({
  minHeight: 32,
  padding: theme.spacing(0.625, 1.75),
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  lineHeight: 1.25,
  whiteSpace: "nowrap",
  color: theme.palette.colors?.brand700 ?? theme.palette.primary.dark,
  borderColor: theme.palette.colors?.brand300 ?? theme.palette.primary.light,
  borderWidth: 1,
  background: `linear-gradient(135deg, ${
    theme.palette.colors?.brand50 ?? theme.palette.primary.light
  } 0%, rgba(255, 255, 255, 0.95) 100%)`,
  boxShadow: `0 2px 6px ${
    theme.palette.colors?.brand400 ?? theme.palette.primary.main
  }25`,
  "&:hover": {
    borderWidth: 1,
    borderColor: theme.palette.colors?.brand500 ?? theme.palette.primary.main,
    background: `linear-gradient(135deg, ${
      theme.palette.colors?.brand100 ?? theme.palette.primary.light
    } 0%, rgba(255, 255, 255, 1) 100%)`,
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(0.75),
  },
  "& .MuiButton-startIcon .MuiSvgIcon-root": {
    fill: "#FFD700",
  },
}))

const MlsChip = styled(Chip)(({ theme }) => ({
  height: 24,
  maxWidth: "100%",
  fontWeight: 600,
  borderRadius: 8,
  backgroundColor: theme.palette.colors?.brand50 ?? theme.palette.primary.light,
  color: theme.palette.colors?.brand700 ?? theme.palette.primary.dark,
  border: `1px solid ${
    theme.palette.colors?.brand200 ?? theme.palette.primary.light
  }`,
  "& .MuiChip-label": {
    px: 1,
    fontSize: "0.8125rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}))

/**
 * Compact branded header for the My MLS agent list.
 *
 * @param {object} props
 * @param {string[]} props.mlsNames
 * @param {boolean} props.mlsSelected
 * @param {() => void} [props.onBuyMoreMlsClick]
 * @param {boolean} [props.showPurchaseMlsButton]
 * @param {(criteria: object) => void | Promise<void>} props.onCampaignCriteriaApply
 * @param {() => void} [props.onCampaignCriteriaSaved]
 */
export default function MyMlsPageHeader({
  mlsNames,
  mlsSelected,
  onBuyMoreMlsClick,
  showPurchaseMlsButton = false,
  onCampaignCriteriaApply,
  onCampaignCriteriaSaved,
}) {
  const [boostCampaignDialogOpen, setBoostCampaignDialogOpen] = useState(false)
  const openedLeadTargetingFromRedirectRef = useRef(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const profile = useSelector(selectProfile)
  const showLeadTargeting = canAccessLeadTargeting(profile)

  useEffect(() => {
    if (!showLeadTargeting) {
      return
    }
    if (searchParams.get(MY_MLS_SEARCH_PARAMS.openLeadTargeting) !== "1") {
      return
    }
    if (openedLeadTargetingFromRedirectRef.current) {
      return
    }
    openedLeadTargetingFromRedirectRef.current = true

    const next = new URLSearchParams(searchParams)
    next.delete(MY_MLS_SEARCH_PARAMS.openLeadTargeting)
    const qs = next.toString()
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
      replace: true,
    })

    setBoostCampaignDialogOpen(true)
  }, [searchParams, navigate, location.pathname, showLeadTargeting])

  const hasMlsNames = mlsNames.length > 0
  const { hasSubmitted: hasCampaignProfile } = useBrokerageProfileIntake()

  return (
    <HeaderRoot>
      <IconBadge aria-hidden>
        <PersonAddOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
      </IconBadge>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Row
          sx={{
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
            rowGap: 0.25,
          }}
        >
          <Typography
            variant="subtitle2"
            component="span"
            fontWeight={700}
            color="text.primary"
            sx={{ lineHeight: 1.3 }}
          >
            {mlsSelected && hasMlsNames ? "Agents in" : "My Leads"}
          </Typography>
          {mlsSelected && hasMlsNames
            ? mlsNames.map((name) => (
                <MlsChip key={name} label={name} size="small" title={name} />
              ))
            : null}
        </Row>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mt: 0.25,
            lineHeight: 1.35,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: { xs: "normal", sm: "nowrap" },
          }}
        >
          {mlsSelected
            ? hasCampaignProfile
              ? "Add agents to Candidates, then reach them with your recruiting campaign."
              : "Set up your campaign profile to boost outreach when you add leads."
            : showPurchaseMlsButton
            ? "Purchase MLS access to browse agents in your market."
            : "No MLS active. Ask your account owner to purchase MLS access."}
        </Typography>
      </Box>

      <Row
        sx={{
          gap: 1,
          flexShrink: 0,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
        }}
      >
        {showLeadTargeting ? (
          <HeaderActionButton
            variant="outlined"
            size="small"
            startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={() => setBoostCampaignDialogOpen(true)}
          >
            Lead Targeting for Campaign
          </HeaderActionButton>
        ) : null}
        {showPurchaseMlsButton ? (
          <HeaderActionButton
            variant={mlsSelected ? "outlined" : "contained"}
            size="small"
            onClick={onBuyMoreMlsClick}
            sx={
              mlsSelected
                ? undefined
                : {
                    color: "primary.contrastText",
                    borderColor: "primary.main",
                    background: (theme) => theme.palette.primary.main,
                    boxShadow: (theme) =>
                      `0 2px 6px ${theme.palette.primary.main}40`,
                    "&:hover": {
                      borderColor: "primary.dark",
                      background: (theme) => theme.palette.primary.dark,
                    },
                  }
            }
          >
            Purchase MLS
          </HeaderActionButton>
        ) : null}
      </Row>
      {showLeadTargeting ? (
        <BoostLeadsWithCampaignDialog
          open={boostCampaignDialogOpen}
          onClose={() => setBoostCampaignDialogOpen(false)}
          onApply={onCampaignCriteriaApply}
          onSaved={onCampaignCriteriaSaved}
        />
      ) : null}
    </HeaderRoot>
  )
}
